import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  Lock as LockIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ContactSupport as SupportIcon,
  Security as SecurityIcon,
  VpnKey as KeyIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import useDentalAccess from "../../hooks/useDentalAccess";
import useBDocTranslator from "../../hooks/useBDocTranslator";

const useStyles = makeStyles((theme) => ({
  permissionCard: {
    margin: theme.spacing(2, 0),
    border: `2px solid ${theme.palette.error.main}`,
  },
  warningCard: {
    margin: theme.spacing(2, 0),
    border: `2px solid ${theme.palette.warning.main}`,
  },
  infoCard: {
    margin: theme.spacing(2, 0),
    border: `2px solid ${theme.palette.info.main}`,
  },
  iconContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
  },
  errorIcon: {
    fontSize: 64,
    color: theme.palette.error.main,
  },
  warningIcon: {
    fontSize: 64,
    color: theme.palette.warning.main,
  },
  infoIcon: {
    fontSize: 64,
    color: theme.palette.info.main,
  },
  textCenter: {
    textAlign: "center",
  },
  permissionsList: {
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    margin: theme.spacing(2, 0),
  },
  userInfo: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
}));

/**
 * Componente guard que protege el acceso a funcionalidades dentales
 * Muestra diferentes tipos de mensajes según el nivel de acceso del usuario
 */
function DentalPermissionGuard({
  children,
  requiredLevel = "view",
  strictMode = false,
  patientId = null,
  showUserInfo = false,
  onAccessDenied = null,
  fallbackComponent = null,
}) {
  const classes = useStyles();
  const { t } = useBDocTranslator();

  const dentalAccess = useDentalAccess({
    requiredLevel,
    strictMode,
    patientId,
  });

  // Si el usuario cumple con los requisitos, mostrar el contenido
  if (dentalAccess.meetsRequiredLevel && !dentalAccess.errorMessage) {
    return <>{children}</>;
  }

  // Si se proporciona un componente fallback personalizado
  if (fallbackComponent) {
    return fallbackComponent;
  }

  // Determinar el tipo de restricción y mostrar el mensaje apropiado
  const getRestrictionType = () => {
    if (!dentalAccess.canView) return "access_denied";
    if (requiredLevel === "edit" && !dentalAccess.canEdit) return "read_only";
    if (requiredLevel === "admin" && !dentalAccess.isAdmin)
      return "admin_required";
    if (strictMode && dentalAccess.strictValidations) {
      const validations = dentalAccess.strictValidations;
      if (!validations.hasActiveStatus) return "inactive_user";
      if (!validations.hasValidLicense) return "invalid_license";
      if (!validations.hasValidWorkLocation) return "missing_location";
    }
    return "general_restriction";
  };

  const restrictionType = getRestrictionType();

  const renderUserInfo = () => {
    if (!showUserInfo) return null;

    return (
      <Box className={classes.userInfo}>
        <Typography variant="subtitle2" gutterBottom>
          {t("Información del Usuario")}
        </Typography>
        <Typography variant="body2">
          <strong>{t("Nombre")}:</strong> {dentalAccess.user.name}
        </Typography>
        <Typography variant="body2">
          <strong>{t("Rol")}:</strong> {dentalAccess.user.role}
        </Typography>
        <Typography variant="body2">
          <strong>{t("Especialidad")}:</strong> {dentalAccess.user.specialty}
        </Typography>
        {dentalAccess.user.licenseNumber && (
          <Typography variant="body2">
            <strong>{t("Licencia")}:</strong> {dentalAccess.user.licenseNumber}
          </Typography>
        )}
      </Box>
    );
  };

  const renderPermissionsList = () => {
    const permissions = dentalAccess.getPermissionSummary();

    return (
      <Box className={classes.permissionsList}>
        <Typography variant="subtitle2" gutterBottom>
          {t("Permisos Actuales")}
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              {permissions.permissions.view ? (
                <KeyIcon color="primary" />
              ) : (
                <LockIcon color="error" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={t("Visualizar")}
              secondary={
                permissions.permissions.view ? t("Permitido") : t("Denegado")
              }
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              {permissions.permissions.edit ? (
                <KeyIcon color="primary" />
              ) : (
                <LockIcon color="error" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={t("Editar")}
              secondary={
                permissions.permissions.edit ? t("Permitido") : t("Denegado")
              }
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              {permissions.permissions.create ? (
                <KeyIcon color="primary" />
              ) : (
                <LockIcon color="error" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={t("Crear")}
              secondary={
                permissions.permissions.create ? t("Permitido") : t("Denegado")
              }
            />
          </ListItem>
        </List>
      </Box>
    );
  };

  const getRestrictionContent = () => {
    switch (restrictionType) {
      case "access_denied":
        return {
          severity: "error",
          icon: <LockIcon className={classes.errorIcon} />,
          title: t("Acceso Denegado"),
          message: dentalAccess.errorMessage,
          cardClass: classes.permissionCard,
          action: onAccessDenied ? (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<SupportIcon />}
              onClick={onAccessDenied}
            >
              {t("Contactar Soporte")}
            </Button>
          ) : null,
        };

      case "read_only":
        return {
          severity: "warning",
          icon: <WarningIcon className={classes.warningIcon} />,
          title: t("Acceso Limitado"),
          message: t(
            "Solo tiene permisos de lectura. Para editar necesita especialidad dental."
          ),
          cardClass: classes.warningCard,
          action: (
            <Button variant="outlined" color="primary" startIcon={<InfoIcon />}>
              {t("Más Información")}
            </Button>
          ),
        };

      case "admin_required":
        return {
          severity: "warning",
          icon: <SecurityIcon className={classes.warningIcon} />,
          title: t("Permisos de Administrador Requeridos"),
          message: t(
            "Esta funcionalidad requiere permisos de administrador del sistema."
          ),
          cardClass: classes.warningCard,
        };

      case "inactive_user":
        return {
          severity: "error",
          icon: <LockIcon className={classes.errorIcon} />,
          title: t("Usuario Inactivo"),
          message: t(
            "Su cuenta está inactiva. Contacte al administrador del sistema."
          ),
          cardClass: classes.permissionCard,
        };

      case "invalid_license":
        return {
          severity: "error",
          icon: <WarningIcon className={classes.errorIcon} />,
          title: t("Licencia Médica Inválida"),
          message: t(
            "Su licencia médica ha expirado o no es válida. Actualice su información."
          ),
          cardClass: classes.permissionCard,
        };

      case "missing_location":
        return {
          severity: "warning",
          icon: <InfoIcon className={classes.warningIcon} />,
          title: t("Ubicación de Trabajo Requerida"),
          message: t(
            "Debe configurar su ubicación de trabajo para acceder al módulo dental."
          ),
          cardClass: classes.warningCard,
        };

      default:
        return {
          severity: "info",
          icon: <InfoIcon className={classes.infoIcon} />,
          title: t("Acceso Restringido"),
          message:
            dentalAccess.errorMessage ||
            t("No tiene los permisos necesarios para esta acción."),
          cardClass: classes.infoCard,
        };
    }
  };

  const restrictionContent = getRestrictionContent();

  return (
    <Card className={restrictionContent.cardClass}>
      <CardContent>
        <div className={classes.iconContainer}>{restrictionContent.icon}</div>

        <Typography variant="h5" className={classes.textCenter} gutterBottom>
          {restrictionContent.title}
        </Typography>

        <Alert
          severity={restrictionContent.severity}
          style={{ marginBottom: 16 }}
        >
          {restrictionContent.message}
        </Alert>

        {showUserInfo && renderUserInfo()}

        <Divider style={{ margin: "16px 0" }} />

        {renderPermissionsList()}

        <Box className={classes.textCenter}>
          <Chip
            label={`${t("Nivel Requerido")}: ${requiredLevel.toUpperCase()}`}
            color="primary"
            size="small"
            style={{ marginRight: 8 }}
          />
          <Chip
            label={`${t(
              "Nivel Actual"
            )}: ${dentalAccess.accessLevel.toUpperCase()}`}
            color="default"
            size="small"
          />
        </Box>

        {restrictionContent.action && (
          <div className={classes.actionButtons}>
            {restrictionContent.action}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default DentalPermissionGuard;
